import { Component } from 'react';

import { RootState } from '@iwProvider/configureStore';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { FieldArray, formValueSelector } from 'redux-form';

import { getApplicationStatus } from '../../../../api/lendingAPI/ApplicationSelector';
import { FormField } from '../../../../forms/formField/FormField';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';
import { PlusSVG } from '../../../../svgComponents/plusSVG/index';
import { TrashcanSVG } from '../../../../svgComponents/trashcanSVG/index';
import {
    ADDRESS_FETCH_REQUESTED,
    TOGGLE_MANUAL_ADDRESS_STATE
} from '../PersonModal.actions';
import AddressQuestion from './AddressQuestion';
import { ResidentialStatusQuestion } from './ResidentialStatusQuestion';
import styles from '../PersonModal.module.css';

const selector = formValueSelector('person');
const mapStateToProps = (state: RootState) => ({
    ...state.personModal,
    hasCreditDecision: getApplicationStatus(state) !== 'open',
    isGuarantor: selector(state, 'isGuarantor'),
    isApplicant: selector(state, 'isApplicant'),
    companyType: state.profile.companyType.value
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    fetchAddresses: (postcode: string) =>
        dispatch({ type: ADDRESS_FETCH_REQUESTED, postcode }),
    toggleManualAddressState: (addressIndex: number) =>
        dispatch({ type: TOGGLE_MANUAL_ADDRESS_STATE, addressIndex })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

class AddressDetails extends Component<PropsFromRedux> {
    handleAddAddress = (fields: any) => {
        return () => {
            fields.push({ isPrimaryAddress: false, showManualAddress: false });
        };
    };

    handleRemoveAddress(fields: any, index: number) {
        return () => {
            fields.remove(index);
        };
    }

    handleIsPrimaryAddress(index: number) {
        return this.props.addresses[index]?.isPrimaryAddress;
    }

    renderResidentialStatusQuestion(addressField: string, index: number) {
        const isPrimaryAddress = this.handleIsPrimaryAddress(index);

        if (!isPrimaryAddress) {
            return null;
        }

        if (
            (getCountry() === 'UK' &&
                this.props.companyType === 'sole_trader') ||
            getCountry() === 'DE'
        ) {
            return (
                <ResidentialStatusQuestion
                    data-testid="residential_status"
                    name={`${addressField}.residential_status`}
                />
            );
        }

        return null;
    }

    renderRemoveAddressBtn(fields: any, index: number) {
        const isPrimaryAddress = this.handleIsPrimaryAddress(index);

        if (!isPrimaryAddress) {
            return (
                <div
                    className={styles.removeBtn}
                    onClick={this.handleRemoveAddress(fields, index)}
                    data-testid="remove-address-button"
                >
                    <TrashcanSVG />
                </div>
            );
        }

        return null;
    }

    calculateAddressStyles(index: number) {
        if (index === 0) {
            return styles.addressWrapper;
        }

        return classNames(styles.removeWrapper, styles.previousAddressWrapper);
    }

    buiidAddressField(addressField: string, fields: any, index: number) {
        const addressStyles = this.calculateAddressStyles(index);
        const residentialStatusQuestion = this.renderResidentialStatusQuestion(
            addressField,
            index
        );
        const removeAddressBtn = this.renderRemoveAddressBtn(fields, index);

        return (
            <div
                className={addressStyles}
                key={index}
                data-testid="address-question"
            >
                <AddressQuestion
                    name={addressField}
                    onManualAddressToggle={this.props.toggleManualAddressState}
                    fetchedAddresses={this.props.addresses}
                    onChange={this.props.fetchAddresses}
                    addressIndex={index}
                />
                {residentialStatusQuestion}
                {removeAddressBtn}
            </div>
        );
    }

    buildAddressFields = ({ fields }: any) => {
        const disableEditingAdditionalGuarantor =
            this.props.hasCreditDecision &&
            this.props.isEditingPerson &&
            this.props.isGuarantor &&
            !this.props.isApplicant;

        return (
            <>
                {fields.map((addressField: string, index: number) => {
                    return this.buiidAddressField(addressField, fields, index);
                })}
                {getCountry() === 'UK' &&
                    !disableEditingAdditionalGuarantor && (
                        <button
                            className={styles.addBtn}
                            type="button"
                            data-testid="add-another-address-button"
                            onClick={this.handleAddAddress(fields)}
                        >
                            <FormattedMessage
                                id="_.person.add_another_address"
                                defaultMessage="Add previous address"
                            />
                            <PlusSVG className={styles.addIcon} />
                        </button>
                    )}
            </>
        );
    };

    render() {
        return (
            <>
                <div className={styles.subTitle}>
                    <FormattedMessage
                        id="_.person.address_details"
                        defaultMessage="Address details"
                    />
                </div>
                <FieldArray
                    name="addresses"
                    component={this.buildAddressFields}
                />
                <FormField
                    className={styles.marketingCheckbox}
                    name="marketing"
                    data-testid="marketing"
                    type="checkbox"
                    label={
                        <FormattedMessage
                            id="_.person.marketing"
                            defaultMessage="News and updates from iwoca"
                        />
                    }
                />
            </>
        );
    }
}

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(AddressDetails);
